import { Button, Alert } from '@tunasong/ui-lib'
import { Check, Fail } from '@tunasong/icons'
import { GuitarLib, NoteLib, ScaleLib } from '@tunasong/music-lib'
import { Fretboard, type FretboardProps } from '@tunasong/music-ui'
import { standardNoteNames, type NoteName, type Scale } from '@tunasong/schemas'
import { isMobileDevice, Typography, VBox } from '@tunasong/ui-lib'
import type { FC, HTMLAttributes, JSX } from 'react'
import { useRef, useState } from 'react'
import invariant from 'tiny-invariant'
import type { CAGEDGame as CAGEDGameType } from '../types.js'
import styles from './caged-game.module.css'
import { getAction, type GameAction } from './game-action.js'
import { useLeaderboard } from './leaderboard.hook.js'
import { getRandomCagedShape } from './lib/random-shape.js'
import ScorePanel from './score-panel.js'
import { useScorePersist } from './score-persist.js'
import { useScorer } from './scorer.hook.js'

export interface CAGEDGameProps extends HTMLAttributes<HTMLDivElement> {
  /** The game */
  game: CAGEDGameType
  /** The delay between rounds, in MS */
  roundDelayMS?: number
}

/**
 *
 * Goals per experience level: @see https://www.notion.so/Guitar-Fretboard-Game-Mastery-Level-System-1f38e623975c80c3a804fda0930b825f?pvs=4
 */

/** Action types for the CAGED game */
type CAGEDActionType = 'shape' | 'rootPosition' | 'rootNote'

const cagedGameActions = [
  {
    id: 'shape',
    name: 'Name the shape',
    description: 'You are shown a CAGED shape somewhere on the fretboard. Click the correct name.',
    scoreMultiplier: 1,
    minProficiency: 1,
    maxProficiency: 2,
  },
  {
    id: 'rootPosition',
    name: 'Position a root note in the shape',
    description:
      'You are shown a CAGED shape somewhere on the fretboard. Click on a correct root note in the shape on the fretboard. There may be multiple root notes in the shape.',
    scoreMultiplier: 1.5,
    minProficiency: 2,
    maxProficiency: 5,
  },
  {
    id: 'rootNote',
    name: 'Name the root note / major scale name for the shape',
    scoreMultiplier: 1.5,
    description:
      'You are shown a CAGED shape somewhere on the fretboard. The root note is highlighted. Name the root note and the major scale name for the shape (i.e., the I degree note).',
    minProficiency: 2,
    maxProficiency: 3,
  },
  /** @todo
   *
   * - Name the degree of the scale (i.e., I, II, III, IV, V, VI, VII), focusing on the root, major third, and perfect fifth.
   */
] as const satisfies GameAction<CAGEDActionType>[]

export const CAGEDGame: FC<CAGEDGameProps> = props => {
  const { game, roundDelayMS = 5000, ...boxProps } = props

  const frets = isMobileDevice() ? 14 : 24

  const [icons, setIcons] = useState<Partial<Record<NoteName, JSX.Element | undefined>>>({})
  const [running, setRunning] = useState(false)
  const [currentShape, setCurrentShape] = useState<'C' | 'A' | 'G' | 'E' | 'D'>()
  const [currentRoot, setCurrentRoot] = useState<NoteName>()
  const [fingerings, setFingerings] = useState<GuitarLib.FingeringWithMetadata[]>()
  const [labels, setLabels] = useState<FretboardProps['labels']>('none')
  const [colors, setColors] = useState(false)
  // The last round result - either true (correct) or false (incorrect)
  const [lastRoundResult, setLastRoundResult] = useState<boolean>()
  const [endRoundMessage, setEndRoundMessage] = useState<string>()
  const [showEndRoundMessage, setShowEndRoundMessage] = useState(false)
  const currentScale = currentRoot ? ({ root: currentRoot, type: 'major' } satisfies Scale) : undefined
  const [currentAction, setCurrentAction] = useState<GameAction<CAGEDActionType>>()
  const leaderBoard = useLeaderboard({ game })

  const { userGameResults, saveScore } = useScorePersist({ game, userName: name ?? 'Unknown' })

  const { score, correct, fail, bonusTime, resetBonusTime, resetScore, userStats } = useScorer({
    action: currentAction,
    userGameResults,
    saveScore,
  })

  const clearGame = () => {
    setIcons({})
    setCurrentAction(undefined)
    setLastRoundResult(undefined)
    setCurrentShape(undefined)
    setCurrentRoot(undefined)
    setFingerings(undefined)
    setColors(false)
    setLabels('none')
    setEndRoundMessage(undefined)
    setShowEndRoundMessage(false)
    if (timeout.current) {
      clearTimeout(timeout.current)
    }
  }

  const updateGame = () => {
    const action = getAction({
      gameActions: cagedGameActions,
      proficiency: userStats?.level.proficiency ?? 1,
    })
    invariant(action, 'Action should be defined')

    clearGame()

    setCurrentAction(action)

    if (action.id === 'shape') {
      const { shape, rootNote, shapeName } = getRandomCagedShape('major', frets)
      const scale = { root: rootNote, type: 'major' } satisfies Scale
      setCurrentShape(shapeName)
      setCurrentRoot(rootNote)
      setEndRoundMessage(`The shape is ${shapeName} in the scale ${ScaleLib.getName(scale)}.`)
      setFingerings(shape.fingerings)
      resetBonusTime(10)
    } else if (action.id === 'rootPosition') {
      const { shape, rootNote, shapeName } = getRandomCagedShape('major', frets)
      setEndRoundMessage(
        `The shape is ${shapeName} in the scale ${ScaleLib.getName({ root: rootNote, type: 'major' })}.`
      )
      setCurrentShape(shapeName)
      setCurrentRoot(rootNote)
      setFingerings(shape.fingerings)
      resetBonusTime(10)
    } else if (action.id === 'rootNote') {
      // @todo make a new game here
      const { shape, rootNote, shapeName } = getRandomCagedShape('major', frets)
      setEndRoundMessage(
        `The scale is ${ScaleLib.getName({ root: rootNote, type: 'major' })} in the ${shapeName} shape.`
      )
      setColors(true)
      setLabels('degree')
      setCurrentShape(shapeName)
      setCurrentRoot(rootNote)
      setFingerings(shape.fingerings)
      resetBonusTime(10)
    } else {
      throw new Error(`Unknown action: ${action.id}`)
    }
  }

  const timeout = useRef<number | NodeJS.Timeout>(null)

  const startRound = () => {
    setRunning(true)
    resetScore()
    updateGame()
  }

  const nextAction = (running: boolean) => {
    if (timeout.current) {
      clearTimeout(timeout.current)
    }
    resetBonusTime(0)
    setRunning(running)

    setShowEndRoundMessage(true)

    timeout.current = setTimeout(() => {
      setIcons({})
      if (running) {
        updateGame()
      } else {
        clearGame()
      }
    }, roundDelayMS)
  }

  const handleCAGEDClick = (n: NoteName) => () => {
    if (!currentShape) {
      return
    }
    let newRunningState = running
    /** Add labels and colors to help learn these */
    setLabels('note')
    setColors(true)
    if (NoteLib.equals(n, currentShape)) {
      setIcons(icons => ({ ...icons, [n]: <Check color="var(--tuna-palette-success-main)" /> }))
      setLastRoundResult(true)
      correct()
    } else {
      setIcons(icons => ({ ...icons, [n]: <Fail color="var(--tuna-palette-error-main)" /> }))
      // Flag the correct answer
      setIcons(icons => ({ ...icons, [currentShape]: <Check color="var(--tuna-palette-success-main)" /> }))
      setLastRoundResult(false)
      newRunningState = false
      fail()
    }
    nextAction(newRunningState)
  }

  const handleFretboardClick = (selectedFingering: GuitarLib.FingeringWithMetadata) => {
    let newRunningState = running
    /** Show colors, but not the labels since we're focusing on the root note */

    if (currentAction?.id === 'rootPosition') {
      setLabels('none')
      setColors(true)
      // Check that the clicked fingering corresponds to the root note in the current shape
      if (selectedFingering.scaleDegree === 'I') {
        setLastRoundResult(true)
        correct()
      } else {
        setLastRoundResult(false)
        newRunningState = false
        fail()
      }
      nextAction(newRunningState)
    }
  }

  const handleScaleClick = (scale: Scale) => () => {
    let newRunningState = running
    if (currentAction?.id === 'rootNote') {
      // Check that the clicked fingering corresponds to the root note in the current shape
      /** @todo handles only major scales currently. We need the same for minor*/
      if (currentRoot === scale.root) {
        setIcons(icons => ({ ...icons, [currentRoot]: <Check color="var(--tuna-palette-success-main)" /> }))

        setLastRoundResult(true)
        correct()
      } else {
        setLastRoundResult(false)
        if (currentRoot) {
          setIcons(icons => ({ ...icons, [currentRoot]: <Check color="var(--tuna-palette-success-main)" /> }))
        }
        newRunningState = false
        fail()
      }
      nextAction(newRunningState)
    }
  }

  return (
    <VBox {...boxProps}>
      <Typography variant="h2" className={styles.heading}>
        {currentAction?.name}
      </Typography>

      <div>
        <Fretboard
          fingerings={fingerings}
          start={1}
          end={frets}
          labels={labels}
          scale={currentScale}
          colors={colors}
          onSelectFingering={handleFretboardClick}
        />
      </div>

      {running && !showEndRoundMessage ? (
        <Alert severity={'info'} className={styles.alertSpacing}>
          <Typography variant="body1" className={styles.alertBody}>
            {currentAction?.description}
          </Typography>
        </Alert>
      ) : null}

      {/* End of round message */}
      {showEndRoundMessage && endRoundMessage ? (
        <Alert severity={lastRoundResult ? 'success' : 'error'} className={styles.alertSpacing}>
          <Typography variant="body1" className={styles.alertBody}>
            {lastRoundResult ? 'Correct! ' : 'Incorrect! '}
            {endRoundMessage}
          </Typography>
        </Alert>
      ) : null}

      {/* @todo factor out into a ShapeSelector component  */}
      {running ? (
        <>
          {currentAction?.id === 'shape' ? (
            <div className={styles.shapeGrid}>
              {(['C', 'A', 'G', 'E', 'D'] satisfies NoteName[]).map(n => (
                <Button variant="outlined" key={n} onClick={handleCAGEDClick(n)} endIcon={icons[n]}>
                  {n}
                </Button>
              ))}
            </div>
          ) : null}
          {currentAction?.id === 'rootNote' ? (
            <div className={styles.noteGrid}>
              {standardNoteNames.map(n => (
                <Button
                  variant="outlined"
                  key={n}
                  onClick={handleScaleClick({
                    type: 'major',
                    root: n,
                  })}
                  endIcon={icons[n]}
                  className={styles.noteButton}
                >
                  {NoteLib.prettifyNote(n)} major
                </Button>
              ))}
            </div>
          ) : null}
        </>
      ) : (
        <Button variant="contained" onClick={startRound} className={styles.startButton}>
          Start Game
        </Button>
      )}
      {userStats ? (
        <ScorePanel score={score} leaderBoard={leaderBoard} userStats={userStats} game={game} bonusTime={bonusTime} />
      ) : null}
    </VBox>
  )
}

export default CAGEDGame
